<template>
  <div class="login-container">
  <!-- Formulario de inicio de sesión -->
  <form @submit.prevent="handleLogin" autocomplete="on">
      <h2>{{ $t('inicio_sesion') }}</h2>

  <!-- Campo de usuario -->
  <input id="login-username" name="username" v-model="nombre_usuario" type="text" :placeholder="$t('nombre_usuario')" autocomplete="username" autocorrect="off" autocapitalize="off" required />

      <!-- Campo de contraseña con icono de ojo -->
      <div class="password-container">
        <div class="password-row">
          <input
            id="login-password"
            name="password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            :placeholder="$t('contrasena')"
            autocomplete="current-password"
            required
          />
          <span class="material-icons password-eye" @click="togglePassword">
            {{ showPassword ? 'visibility_off' : 'visibility' }}
          </span>
        </div>
      </div>

      <button type="submit">{{ $t('acceder') }}</button>
    </form>
  </div>
</template>

<script>
import api from '@/api';
import Swal from 'sweetalert2';
import { useUserStore } from '@/stores/user';
import { setUserPrefs, removeUserPrefs } from '@/services/preferences';
// Not storing credentials: disable Credential Management and persistent account storage to avoid "remember credentials" prompts

export default {
  setup() {
    const userStore = useUserStore();
    return { userStore };
  },
  mounted() {
    // No recuperar ni autocompletar credenciales: el formulario queda en blanco para que el usuario las introduzca cada vez.
  },
  data() {
    return {
      nombre_usuario: '',
      password: '',
      showPassword: false,
    };
  },
  methods: {
    togglePassword() {
      this.showPassword = !this.showPassword;
    },

    async handleLogin() {
      try {
        const response = await api.post('/login', {
          nombre_usuario: this.nombre_usuario,
          password: this.password,
        });

        // Usuario bloqueado
        if (response.data.user?.bloqueado) {
          return Swal.fire({
            icon: 'error',
            title: this.$t('USER_BLOCKED'),
            toast: false,
            position: 'center',
            background: '#99172A',
            color: '#fff',
            showConfirmButton: true,
            confirmButtonText: this.$t('aceptar') || 'Aceptar',
            allowOutsideClick: false,
            allowEscapeKey: false,
          });
        }

        // Cambio de contraseña requerido
        if (response.data.debe_cambiar_password) {
          // guardar token temporalmente para que la instancia api inyecte Authorization
          sessionStorage.setItem('auth_token', response.data.token);
          return this.solicitarCambioPassword(this.password);
        }

        // Login exitoso
        this.userStore.setUser({
          name: response.data.user.nombre_completo,
          role: response.data.user.rol_id,
          token: response.data.token,
        });

        // Guardar valores directamente desde la respuesta (backend ya devuelve user.id y rol_id)
        if (response?.data?.user) {
          const user = response.data.user;
          if (user.id != null) sessionStorage.setItem('user_id', String(user.id));
          if (user.rol_id != null) sessionStorage.setItem('user_role', String(user.rol_id));
          if (response.data.token) sessionStorage.setItem('auth_token', String(response.data.token));
          if (user.nombre_completo) sessionStorage.setItem('user_name', String(user.nombre_completo));
          // persistir nativamente
          setUserPrefs({ auth_token: response.data.token, user_name: user.nombre_completo, user_role: user.rol_id, user_id: user.id }).catch(e => console.warn('setUserPrefs failed', e));
          // No guardar credenciales: no invocamos Credential Management API ni almacenamiento nativo cifrado
        }
        
        await Swal.fire({
          icon: 'success',
          title: this.$t('LOGIN_SUCCESS'),
          toast: true,
          position: 'center',
          background: '#28a745',
          color: '#fff',
          showConfirmButton: false,
          confirmButtonText: this.$t('aceptar') || 'Aceptar',
          timer: 3000,
        });

        this.$router.push({ name: 'Welcome' });
      } catch (error) {
        // Normalizar respuesta: axios error, wrapper {status,data} o XHR raw
        let resp = null;
        if (error?.response) {
          resp = error.response;
        } else if (typeof error === 'object' && error?.status && error?.data) {
          resp = { status: error.status, data: error.data };
        } else if (error?.request?.response) {
          try {
            resp = { data: JSON.parse(error.request.response), status: error.request.status || undefined };
          } catch (e) {
            resp = { data: { message: error.request.response }, status: error.request.status || undefined };
          }
        }

        if (!resp) {
          return Swal.fire({ icon: 'error', title: this.$t('intentalo_de_nuevo'), toast: true, position: 'center', timer: 3000, showConfirmButton: false });
        }

        // Response procesada (se omite log por seguridad)
        
        // Toast habitual (no usar allowOutsideClick/allowEscapeKey con toast)
        const errorKey = resp?.data?.error || 'intentalo_de_nuevo';
        const mensaje = this.$t(errorKey, null, { defaultValue: resp?.data?.message || this.$t('intentalo_de_nuevo') });

        await Swal.fire({
          icon: 'error',
          title: mensaje,
          toast: true,
          position: 'center',
          background: '#99172A',
          color: '#fff',
          showConfirmButton: false,
          timer: 3000,
        });

        // Caso especial: exactamente { error: "INVALID_CREDENTIALS", user: { bloqueado: false }, recien_bloqueado: true }
        if (resp?.data?.recien_bloqueado === true && resp?.data?.user?.bloqueado === false) {
          console.log('Condición especial cumplida: mostrar segundo SweetAlert. response.data:', resp.data);
          const bloqueoMsg = resp.data?.message || this.$t('USER_BLOCKED_RECENT') || 'Tu cuenta ha sido bloqueada y se ha creado una incidencia.';
          const ticketId = resp.data?.ticket_id || (resp.data?.ticket && resp.data.ticket.id) || null;
          const fullMsg = ticketId ? `${bloqueoMsg} (${this.$t('ticket') || 'Ticket'} #${ticketId})` : bloqueoMsg;
          await Swal.fire({
            icon: 'warning',
            title: this.$t('USER_BLOCKED_RECENT_TITLE') || this.$t('USER_BLOCKED'),
            text: fullMsg,
            toast: false,
            position: 'center',
            showConfirmButton: false,
            timer: 7000,
            timerProgressBar: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
          });
        }
      }
    },

    async solicitarCambioPassword(currentPassword) {
      const result = await Swal.fire({
        title: `<div class="swal2-titulo-corporativo">${this.$t('cambio_contrasena_requerido')}</div>`,
        html: `
          <style>
            .swal2-titulo-corporativo {
              background: #99172A;
              color: #fff;
              padding: 16px 0;
              font-size: 22px;
              font-weight: bold;
              border-radius: 8px 8px 0 0;
              margin: -20px -20px 20px -20px;
            }
            .swal2-input { margin-bottom: 10px; }
            .swal2-password-row {
              position: relative;
              display: flex;
              align-items: center;
              margin-bottom: 10px;
            }
            .swal2-password-row input {
              flex: 1;
              padding: 10px;
            }
            .swal2-eye {
              position: absolute;
              right: 10px;
              cursor: pointer;
              color: #99172A;
              font-size: 20px;
              user-select: none;
            }
          </style>

          <input id="swal-cp-actual" type="hidden" value="${currentPassword}">

          <div class="swal2-password-row">
            <input id="swal-cp-nueva" type="password" class="swal2-input" placeholder="${this.$t('nueva_contrasena')}">
            <span class="material-icons swal2-eye" onclick="
              const input=this.previousElementSibling;
              input.type = input.type==='password' ? 'text' : 'password';
              this.textContent = input.type==='password' ? 'visibility' : 'visibility_off';
            ">visibility</span>
          </div>

          <div class="swal2-password-row">
            <input id="swal-cp-confirm" type="password" class="swal2-input" placeholder="${this.$t('confirmar_nueva_contrasena')}">
            <span class="material-icons swal2-eye" onclick="
              const input=this.previousElementSibling;
              input.type = input.type==='password' ? 'text' : 'password';
              this.textContent = input.type==='password' ? 'visibility' : 'visibility_off';
            ">visibility</span>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: `<span style="color:#fff;">${this.$t('cambiar_contrasena')}</span>`,
        cancelButtonText: this.$t('cancelar'),
        confirmButtonColor: '#99172A',
        allowOutsideClick: false,
        allowEscapeKey: false,
        preConfirm: () => {
          const current_password = document.getElementById('swal-cp-actual').value;
          const new_password = document.getElementById('swal-cp-nueva').value;
          const new_password_confirmation = document.getElementById('swal-cp-confirm').value;

          if (!new_password || !new_password_confirmation) {
            Swal.showValidationMessage(this.$t('todos_los_campos_obligatorios'));
            return false;
          }
          if (new_password !== new_password_confirmation) {
            Swal.showValidationMessage(this.$t('contrasenas_no_coinciden'));
            return false;
          }
          if (
            new_password.length < 12 ||
            !/[a-z]/.test(new_password) ||
            !/[A-Z]/.test(new_password) ||
            !/\d/.test(new_password) ||
            !/[@$!%*?&]/.test(new_password)
          ) {
            Swal.showValidationMessage(this.$t('contrasena_no_valida'));
            return false;
          }
          return { current_password, new_password, new_password_confirmation };
        },
      });

      if (result.isConfirmed && result.value) {
        try {
          // usar api.js (token ya en localStorage)
          await api.post('/users/change-password', result.value);

          // Login automático con la nueva contraseña
          const loginResponse = await api.post('/login', {
            nombre_usuario: this.nombre_usuario,
            password: result.value.new_password,
          });

          this.userStore.setUser({
            name: loginResponse.data.user.nombre_completo,
            role: loginResponse.data.user.rol_id,
            token: loginResponse.data.token,
          });

          // Persistir valores tras login automático (cambio de contraseña)
          if (loginResponse?.data?.user) {
            const u = loginResponse.data.user;
            if (loginResponse.data.token) sessionStorage.setItem('auth_token', String(loginResponse.data.token));
            if (u.id != null) sessionStorage.setItem('user_id', String(u.id));
            if (u.rol_id != null) sessionStorage.setItem('user_role', String(u.rol_id));
            if (u.nombre_completo) sessionStorage.setItem('user_name', String(u.nombre_completo));
          }
          
          await Swal.fire({
            icon: 'success',
            title: this.$t('contrasena_cambiada_correctamente'),
            toast: false,
            position: 'center',
            background: '#28a745',
            color: '#fff',
            showConfirmButton: true,
            confirmButtonText: this.$t('aceptar') || 'Aceptar',
          });

          this.$router.push({ name: 'Welcome' });
        } catch (error) {
          await Swal.fire({
            icon: 'error',
            title: this.$t('error_cambiar_contrasena'),
            text: error.response?.data?.message || this.$t('intentalo_de_nuevo'),
            confirmButtonColor: '#99172A',
          });

          this.solicitarCambioPassword(token, currentPassword);
        }
      }
    },
  },
};
</script>

<style scoped>
.login-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100vh;
  padding-top: 50px;
  background-color: #f5f5f5;
  text-align: center;
}

form {
  display: flex;
  flex-direction: column;
  width: 300px;
  gap: 15px;
}

form input {
  width: 100%;
  padding: 10px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-sizing: border-box;
}

.password-container {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.password-row {
  position: relative;
  display: flex;
  align-items: center;
}

.password-eye {
  position: absolute;
  right: 10px;
  cursor: pointer;
  color: #99172A;
  font-size: 22px;
  user-select: none;
}

button {
  padding: 10px;
  font-size: 16px;
  color: #fff;
  background-color: #99172A;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

button:hover {
  background-color: #7A1222;
}
</style>
